import { useMemo } from 'react';
import { Badge, Space, Tag } from 'antd';
import { EditOutlined, UnorderedListOutlined } from '@ant-design/icons';
import type { ColumnsType, TableProps } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { TableRowActionsMenu } from '~/app/components/common/TableRowActionsMenu/TableRowActionsMenu';
import { PMP_TAG_COLOR } from '~/app/components/maintenance/pmp-tag-colors';
import { PlanDetailTaskSubtasks } from '~/app/components/maintenance/preventive/PlanDetailPageContainer/components/PlanDetailTaskSubtasks/PlanDetailTaskSubtasks';
import type { TaskResponseDto } from '@evimo/client/models';
import {
  canEditLibraryTask,
  resolvePartLabels,
} from '~/app/components/maintenance/tasks-triggers/utils';

type UseTasksTabColumnsParams = {
  workTypeMap: Map<string, string>;
  partNameById: Map<string, string>;
  isSuperAdmin: boolean;
  organizationId: string | null | undefined;
  onEdit: (task: TaskResponseDto) => void;
};

export function useTasksTabColumns({
  workTypeMap,
  partNameById,
  isSuperAdmin,
  organizationId,
  onEdit,
}: UseTasksTabColumnsParams): {
  columns: ColumnsType<TaskResponseDto>;
  expandable: TableProps<TaskResponseDto>['expandable'];
} {
  const { t } = useTranslation('maintenance');

  const columns: ColumnsType<TaskResponseDto> = useMemo(
    () => [
      {
        title: t('tasks.name'),
        dataIndex: 'name',
        key: 'name',
        render: (name: string, record) => {
          const subtaskCount = record.subtasks?.length ?? 0;
          return (
            <Space size={8} wrap>
              <span>{name}</span>
              {record.isShared ? (
                <Tag color="blue" style={{ marginInlineEnd: 0 }}>
                  {t('tasks.shared')}
                </Tag>
              ) : null}
              {subtaskCount > 0 ? (
                <Tag
                  icon={<UnorderedListOutlined />}
                  className="plan-detail-subtasks-chip"
                  style={{ marginInlineEnd: 0 }}
                >
                  {t('tasks.subtasks.count', { count: subtaskCount })}
                </Tag>
              ) : null}
            </Space>
          );
        },
      },
      {
        title: t('tasks.workType'),
        dataIndex: 'workTypeId',
        key: 'workTypeId',
        render: (id: string) => {
          const code = workTypeMap.get(id);
          const label = code ? t(`workType.${code}`) : id;
          return (
            <Tag color={PMP_TAG_COLOR.workType} style={{ marginInlineEnd: 0 }}>
              {label}
            </Tag>
          );
        },
      },
      {
        title: t('tasks.parts'),
        dataIndex: 'partIds',
        key: 'parts',
        render: (partIds: string[] | undefined) => {
          const labels = resolvePartLabels(partIds, partNameById);
          if (labels.length === 0) {
            return '—';
          }
          return (
            <Space size={[4, 4]} wrap>
              {labels.map((part) => (
                <Tag
                  key={part.id}
                  color={PMP_TAG_COLOR.part}
                  style={{ marginInlineEnd: 0 }}
                >
                  {part.name}
                </Tag>
              ))}
            </Space>
          );
        },
      },
      {
        title: t('tasks.mandatory'),
        dataIndex: 'mandatory',
        key: 'mandatory',
        render: (val: boolean) =>
          val ? (
            <Badge status="success" text={t('common.yes')} />
          ) : (
            <Badge status="default" text={t('common.no')} />
          ),
      },
      {
        title: t('tasks.requiresShutdown'),
        dataIndex: 'requiresShutdown',
        key: 'requiresShutdown',
        render: (val: boolean) =>
          val ? (
            <Tag color="red">{t('common.yes')}</Tag>
          ) : (
            <Tag>{t('common.no')}</Tag>
          ),
      },
      {
        title: t('tasks.duration'),
        dataIndex: 'estimatedDurationMinutes',
        key: 'duration',
        render: (val: number | null) => (val != null ? `${val} min` : '—'),
      },
      {
        title: t('common.createdAt'),
        dataIndex: 'createdAt',
        key: 'createdAt',
        render: (val: string) => new Date(val).toLocaleDateString(),
      },
      {
        key: 'actions',
        width: 56,
        align: 'center' as const,
        render: (_: unknown, record) =>
          canEditLibraryTask(record, {
            isSuperAdmin,
            organizationId,
          }) ? (
            <TableRowActionsMenu
              ariaLabel={t('tasks.edit')}
              items={[
                {
                  key: 'edit',
                  label: t('tasks.edit'),
                  icon: <EditOutlined />,
                  onClick: () => onEdit(record),
                },
              ]}
            />
          ) : null,
      },
    ],
    [t, workTypeMap, partNameById, isSuperAdmin, organizationId, onEdit],
  );

  const expandable: TableProps<TaskResponseDto>['expandable'] = useMemo(
    () => ({
      rowExpandable: (row) => (row.subtasks?.length ?? 0) > 0,
      expandRowByClick: false,
      expandedRowClassName: () => 'plan-detail-subtasks-row',
      expandedRowRender: (row) => (
        <PlanDetailTaskSubtasks
          taskId={row.id}
          subtaskNames={(row.subtasks ?? []).map((subtask) => subtask.name)}
        />
      ),
    }),
    [],
  );

  return { columns, expandable };
}
